import { createRootRoute, createRoute, createRouter, redirect, useNavigate } from '@tanstack/react-router'
import React from 'react'
import App from '../App'
import MainLayout from '../components/layout/MainLayout'
import WorkspaceSettingsLayout from '../components/layout/WorkspaceSettingsLayout'
import { supabase } from '../hooks/supabase'
import CreateNewPassword from '../pages/Auth/pages/create-new-password'
import ResetPassword from '../pages/Auth/pages/reset-password'
import SignIn from '../pages/Auth/pages/signin'
import SignUp from '../pages/Auth/pages/signup'
import Home from '../pages/HomePage/Home/Home'
import IntentSignals from '../pages/HomePage/IntentSignals/IntentSignals'
import Leads from '../pages/HomePage/Leads/Leads'
import Onboard from '../pages/Onboarding/onboard'
import BillingPage from '../pages/Settings/billing'
import PlanPage from '../pages/Settings/plan'
import ProfilePage from '../pages/Settings/profile'
import NotificationsPage from '../pages/Settings/notifications'
import IntegrationsPage from '../pages/Settings/integrations'

// Add RedirectToHome component
function RedirectToHome() {
  const navigate = useNavigate();
  React.useEffect(() => {
    navigate({ to: '/home' });
  }, [navigate]);
  return null;
}

// Define public routes that don't require authentication
const publicPaths = ['/signin', '/signup', '/reset-password', '/create-new-password']

// Auth guard for protected routes
const authGuard = async () => {
  const currentPath = window.location.pathname
  const urlParams = new URLSearchParams(window.location.search)
  
  // Skip auth check if we're handling a Slack OAuth callback
  if (currentPath === '/slackconnect' && urlParams.has('code')) {
    return {}
  }

  const { data: { session } } = await supabase.auth.getSession()
  const { data: { user } } = await supabase.auth.getUser()
  
  // Check for error parameters in both URL and hash
  const hashParams = new URLSearchParams(window.location.hash.substring(1))
  
  const error = urlParams.get('error') || hashParams.get('error')
  const errorCode = urlParams.get('error_code') || hashParams.get('error_code')
  const errorDescription = urlParams.get('error_description') || hashParams.get('error_description')

  // Handle database error for new user
  if (error === 'server_error' && 
      errorCode === 'unexpected_failure' && 
      errorDescription?.includes('Database error saving new user')) {
    throw redirect({
      to: '/signin',
      search: {
        error: 'Please use your business email to sign in'
      }
    })
  }

  // Allow public paths without authentication
  if (publicPaths.includes(currentPath)) {
    return {}
  }

  // Continue with existing auth logic...
  if (!session || !user) {
    throw redirect({
      to: '/signin',
      search: {
        returnTo: window.location.pathname + window.location.search
      }
    })
  }

  return {}
}

const rootRoute = createRootRoute({
  component: App,
  beforeLoad: authGuard,
  errorComponent: ({ error }) => {
    return (
      <div className="flex items-center justify-center h-screen bg-[#FAFAFA]">
        <div className="w-[400px] bg-white rounded-[16px] shadow-lg p-8 border border-[#EEEEEE]">
          <div className="flex flex-col items-center gap-6">
            {/* Clodexa Logo */}
            <div className="flex items-center gap-2">
              <img 
                src="/src/assets/clodexa-logo.png"
                alt="Clodexa"
                className="w-[32px] h-[32px] rounded-full"
              />
              <span className="text-black text-xl font-bold tracking-wider">
                CLODEXA
              </span>
            </div>

            {/* Error Icon */}
            <div className="w-12 h-12 bg-[#FFE5E5] rounded-full flex items-center justify-center">
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                className="text-[#DC2626]"
              >
                <path 
                  d="M12 9v5M12 17.01l.01-.011M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* Error Message */}
            <div className="text-center">
              <h1 className="text-[18px] font-semibold text-[#222222] mb-2">
                Something went wrong!
              </h1>
              <p className="text-[14px] text-[#5C5E63]">
                {error.message}
              </p>
            </div>

            {/* Action Button */}
            <button 
              onClick={() => window.location.href = '/signin'}
              className="w-full h-[40px] bg-[#29DC5B] text-[#222222] rounded-[10px] 
                text-sm font-normal hover:bg-[#22c55e] transition-colors 
                shadow-[0px_0px_2px_0px_#E0E0E0] flex items-center justify-center gap-2"
            >
              Return to Sign In
            </button>
          </div>
        </div>
      </div>
    )
  },
})

const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'layout',
  component: MainLayout,
  beforeLoad: authGuard,
})

// Home
const indexRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/',
  component: () => {
    const navigate = useNavigate();
    React.useEffect(() => {
      navigate({ to: '/accounts' });
    }, [navigate]);
    return null;
  },
})


const accountsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/accounts',
  validateSearch: (search: Record<string, unknown>) => {
    return {
      intent_signal_id: search.intent_signal_id as string | undefined
    }
  },
  component: Leads,
})

const intentSignalsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/intent-signals',
  component: IntentSignals,
})

const editIntentSignalRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/intent-signals/$signalId',
  component: () => {
    return <IntentSignals isOpen={true} />;
  }
});

// Auth
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signin',
  component: SignIn,
})

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: SignUp,
})

// Onboarding
const onboardLinkedinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/linkedinconnect',
  component: Onboard,
  beforeLoad: authGuard,
})

const onboardCompanyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/companydetails',
  component: Onboard,
  beforeLoad: authGuard,
})

const onboardUserRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/userdetails',
  component: Onboard,
  beforeLoad: authGuard,
})

const onboardSlackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/slackconnect',
  component: Onboard,
  beforeLoad: authGuard,
})

// Settings
const settingsLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'settings-layout',
  component: WorkspaceSettingsLayout,
  beforeLoad: authGuard,
})

const settingsBillingRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/billing',
  component: BillingPage,
})

const settingsPlanRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/plan',
  component: PlanPage,
})

const settingsProfileRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/profile',
  component: ProfilePage,
})

const notificationsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/notifications',
  component: NotificationsPage,
})

const integrationsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/integrations',
  component: IntegrationsPage,
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPassword,
  beforeLoad: async () => {
    const { data: { session } } = await supabase.auth.getSession()
    if (session) {
      throw redirect({
        to: '/',
      })
    }
    return {}
  },
})

const createNewPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/create-new-password',
  component: CreateNewPassword
})

const accountDetailsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/accounts/$leadId',
  component: () => {
    return <Leads isOpen={true} />;
  }
});

const routeTree = rootRoute.addChildren([
  layoutRoute.addChildren([
    indexRoute,
    accountsRoute,
    intentSignalsRoute,
    editIntentSignalRoute,
    accountDetailsRoute,
  ]),
  signInRoute,
  signUpRoute,
  resetPasswordRoute,
  createNewPasswordRoute,
  onboardLinkedinRoute,
  onboardCompanyRoute,
  onboardUserRoute,
  onboardSlackRoute,
  settingsLayoutRoute.addChildren([
    settingsProfileRoute,
    settingsBillingRoute,
    settingsPlanRoute,
    notificationsRoute,
    integrationsRoute,
  ]),
])

const router = createRouter({ routeTree })

export default router 